import { endOfToday, parseISO, startOfDay, subDays } from 'date-fns';
import { useCallback } from 'react';
import { useShallow } from 'zustand/react/shallow';
import LangEnum from '../../../lang/LangEnum';
import useGeneralSettingsStore from '../../../stores/GeneralSettings/useGeneralSettingsStore';
import { formatDate, formatDateFromApi } from '../../../utils/dates';
import { ReferencePeriodEnum } from '../../general-settings/enums/referencePeriod.enum';
import { useTranslation } from '../../translation/TranslationContext';

const useGetSelloutMessage = () => {
  const { translate } = useTranslation();
  const {
    selloutReferencePeriod,
    selloutPeriodLastDaysInterval,
    selloutPeriodCustomIntervalStartDate,
    selloutPeriodCustomIntervalEndDate,
  } = useGeneralSettingsStore(
    useShallow((state) => ({
      selloutReferencePeriod: state.generalSettings.selloutReferencePeriod,
      selloutPeriodLastDaysInterval: state.generalSettings.selloutPeriodLastDaysInterval,
      selloutPeriodCustomIntervalStartDate:
        state.generalSettings.selloutPeriodCustomIntervalStartDate,
      selloutPeriodCustomIntervalEndDate: state.generalSettings.selloutPeriodCustomIntervalEndDate,
    })),
  );

  const getStartAndEndDateSelloutPeriod = useCallback(() => {
    switch (selloutReferencePeriod) {
      case ReferencePeriodEnum.LAST_DAYS: {
        const endDate = endOfToday();
        const startDate = startOfDay(subDays(endDate, selloutPeriodLastDaysInterval));
        return {
          selloutReferencePeriodStartDate: formatDate(startDate),
          selloutReferencePeriodEndDate: formatDate(endDate),
        };
      }
      case ReferencePeriodEnum.CUSTOM_INTERVAL: {
        return {
          selloutReferencePeriodStartDate: formatDate(
            parseISO(formatDateFromApi(selloutPeriodCustomIntervalStartDate)!),
          ),
          selloutReferencePeriodEndDate: formatDate(
            parseISO(formatDateFromApi(selloutPeriodCustomIntervalEndDate)!),
          ),
        };
      }
    }
  }, [
    selloutPeriodCustomIntervalEndDate,
    selloutPeriodCustomIntervalStartDate,
    selloutPeriodLastDaysInterval,
    selloutReferencePeriod,
  ]);

  const getSelloutMessage = useCallback(
    (quantitySold: number) => {
      const { selloutReferencePeriodStartDate, selloutReferencePeriodEndDate } =
        getStartAndEndDateSelloutPeriod();

      return translate(LangEnum.ORDER_SELLOUT_MODAL_RANGE_DATE_DESCRIPTION, {
        quantitySold,
        selloutReferencePeriodStartDate,
        selloutReferencePeriodEndDate,
      });
    },
    [translate, getStartAndEndDateSelloutPeriod],
  );

  const getSelloutModalMessage = useCallback(() => {
    switch (selloutReferencePeriod) {
      case ReferencePeriodEnum.LAST_DAYS: {
        return translate(LangEnum.SELLOUT_STOCK_LAST_DAYS, {
          selloutPeriodLastDaysInterval,
        });
      }
      case ReferencePeriodEnum.CUSTOM_INTERVAL: {
        return `${formatDate(
          parseISO(formatDateFromApi(selloutPeriodCustomIntervalStartDate)!),
        )} - ${formatDate(parseISO(formatDateFromApi(selloutPeriodCustomIntervalEndDate)!))}`;
      }
    }
  }, [
    selloutPeriodCustomIntervalStartDate,
    selloutPeriodCustomIntervalEndDate,
    selloutPeriodLastDaysInterval,
    selloutReferencePeriod,
    translate,
  ]);

  return { getSelloutMessage, getSelloutModalMessage };
};

export default useGetSelloutMessage;
